import { useId, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { cn } from '../cn.js';

export interface DocumentDropzoneProps {
  label: ReactNode;
  /** Shown under the control when idle. */
  hint?: ReactNode;
  /** What the picker accepts, as an `accept` attribute value. */
  accept?: string;
  /** The file currently chosen, if any — the parent owns it. */
  file?: File | null;
  /** Reading is in progress; the control is disabled and says so. */
  busy?: boolean;
  /** Copy shown while `busy` — "Reading the bill…" reads better than a spinner alone. */
  busyLabel?: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
  onFile: (file: File) => void;
  onClear?: () => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Pick or drop one document.
 *
 * KEYBOARD AND POINTER BOTH REACH IT. The drop target is a real `<button>` wrapping a visually
 * hidden `<input type="file">`, not a styled `<div>` with a click handler — so Tab reaches it,
 * Enter and Space open the picker, and a screen reader announces it as a button with a name.
 * Drag-and-drop is an enhancement layered on top of that, never the only way in: a drop target
 * that cannot be operated from the keyboard locks out anyone who does not use a mouse, and
 * uploading a document is not an optional part of onboarding.
 *
 * Named for what it does, not how it looks (CLAUDE.md design tokens): it is a place to put a
 * document, and it stays that when the designs land and it stops being a dashed rectangle.
 */
export function DocumentDropzone({
  label,
  hint,
  accept,
  file,
  busy = false,
  busyLabel,
  error,
  disabled = false,
  onFile,
  onClear,
}: DocumentDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const inert = disabled || busy;

  function take(list: FileList | null) {
    const next = list?.[0];
    if (next) onFile(next);
  }

  function handleDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setDragging(false);
    if (inert) return;
    take(event.dataTransfer.files);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-fg">{label}</span>

      <button
        type="button"
        disabled={inert}
        aria-describedby={cn(hint && hintId, error && errorId) || undefined}
        aria-invalid={error ? true : undefined}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          if (!inert) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed px-4 py-6 text-center transition-colors',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          dragging ? 'border-accent bg-accent-subtle' : 'border-border-strong bg-surface',
          error && !dragging && 'border-danger',
          inert ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:border-accent',
        )}
      >
        {busy ? (
          <span className="text-sm text-fg-muted">{busyLabel ?? 'Reading…'}</span>
        ) : file ? (
          <>
            <span className="text-sm font-medium text-fg">{file.name}</span>
            <span className="text-xs text-fg-muted">{formatSize(file.size)}</span>
          </>
        ) : (
          <>
            <span className="text-sm text-fg">Choose a file or drop it here</span>
            {hint && (
              <span id={hintId} className="text-xs text-fg-muted">
                {hint}
              </span>
            )}
          </>
        )}
      </button>

      {/* Visually hidden rather than `display:none` — a hidden input is not focusable, and some
          assistive tech skips it entirely, so the button above is the accessible control and this
          is only the mechanism it drives. */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          take(event.target.files);
          // Reset so choosing the SAME file twice still fires a change event — a person who
          // retakes a photo under the same name would otherwise see nothing happen.
          event.target.value = '';
        }}
      />

      {file && onClear && !busy && (
        <button
          type="button"
          onClick={onClear}
          className="self-start text-xs text-accent hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          Remove
        </button>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
